import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ChartEmptyState from '../shared/ChartEmptyState'
import Loader from '../shared/Loader'
import ChartInteractions from '../Progression/ChartInteractions'
import Banner from '../ui/Banner'
import Card from '../ui/Card'
import ToggleGroup from '../ui/ToggleGroup'
import EvolutionAreaChart from './EvolutionAreaChart'
import ProgressionService from '../../services/api/ProgressionService'

const CHART_ID = 'evolution-grade-area'

const MODE_OPTIONS = [
  { value: 'grade', label: 'Note' },
  { value: 'satisfaction', label: 'Taux de Satisfaction (%)' }
]

const EMPTY_MESSAGE = 'L\'évolution apparaîtra ici une fois les données disponibles.'
const ERROR_MESSAGE = 'Impossible de charger l\'évolution.'

const LOADING_OVERLAY_CLASSES = 'tw:absolute tw:inset-0 tw:flex tw:items-center '
  + 'tw:justify-center tw:bg-white/70 tw:z-10'

const fetchSeries = ({ collectId, mode, params }) =>
  ProgressionService.fetchMeasure(collectId, null, { name: mode, ...params })

const GradeEvolutionChart = ({
  collectId, gradeMax, dateRange, granularity, mode,
  operatorId, resourceIds, resourceSearch, variableId, headerRight, afterChart, onModeChange
}) => {
  const [buckets, setBuckets] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let cancelled = false
    const params = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      granularity,
      operatorId: operatorId || null,
      resourceIds,
      resourceSearch: resourceSearch || null,
      variableId: variableId || null
    }
    setIsLoading(true)
    fetchSeries({ collectId, mode, params })
      .then(result => { if (!cancelled) {
        setBuckets(result || [])
        setHasError(false)
      } })
      .catch(() => { if (!cancelled) {
        setBuckets([])
        setHasError(true)
      } })
      .finally(() => { if (!cancelled)
        setIsLoading(false) })
    return () => { cancelled = true }
  }, [collectId, dateRange, granularity, mode, operatorId, resourceIds, resourceSearch, variableId])

  const hasNoValue = bucket => bucket.value === null || typeof bucket.value === 'undefined'
  const isEmpty = !isLoading && !hasError && (buckets.length === 0 || buckets.every(hasNoValue))

  const cardHeader = isEmpty && resourceIds.length === 0 ? null : (
    <div className="tw:flex tw:items-center tw:gap-2">
      {headerRight}
      <ToggleGroup
        ariaLabel="Mode d'affichage du chart"
        options={MODE_OPTIONS}
        value={mode}
        onChange={onModeChange}
      />
    </div>
  )

  return (
    <Card title="Évolution de la note" headerRight={cardHeader} plainHeader>
      {hasError ? <Banner variant="danger">{ERROR_MESSAGE}</Banner> : null}
      {!hasError && isEmpty ? <ChartEmptyState message={EMPTY_MESSAGE} /> : null}
      {!hasError && !isEmpty ? (
        <>
          <div className="tw:relative tw:[&_.apexcharts-toolbar]:hidden!">
            <EvolutionAreaChart
              buckets={buckets}
              chartId={CHART_ID}
              gradeMax={gradeMax}
              mode={mode}
            />
            {isLoading ? <div className={LOADING_OVERLAY_CLASSES}><Loader /></div> : null}
          </div>
          <div className="tw:flex">
            <ChartInteractions chartId={CHART_ID} />
          </div>
        </>
      ) : null}
      {afterChart}
    </Card>
  )
}

GradeEvolutionChart.propTypes = {
  afterChart: PropTypes.node,
  collectId: PropTypes.number.isRequired,
  dateRange: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string
  }).isRequired,
  gradeMax: PropTypes.number.isRequired,
  granularity: PropTypes.string.isRequired,
  headerRight: PropTypes.node,
  mode: PropTypes.oneOf(['grade', 'satisfaction']).isRequired,
  onModeChange: PropTypes.func.isRequired,
  operatorId: PropTypes.string,
  resourceIds: PropTypes.arrayOf(PropTypes.number),
  resourceSearch: PropTypes.string,
  variableId: PropTypes.string
}

GradeEvolutionChart.defaultProps = {
  afterChart: null,
  headerRight: null,
  operatorId: '',
  resourceIds: [],
  resourceSearch: '',
  variableId: ''
}

export default GradeEvolutionChart
